"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState, type ReactNode } from "react";
import { ConnectionStatusProvider } from "@sukha/query";
import { phonePresenceVerdict, type PhonePresence } from "@sukha/shared";
import { useDevicePresenceStore } from "@/stores/devicePresenceStore";

/**
 * How long an unanswered question waits before silence counts as absence.
 *
 * ## Why this is not five seconds
 *
 * It was, and it broke the promise the badge already makes. `phone-indicator.spec.ts`
 * exists to stop FALSE drops and drives the disruptions members actually hit: a
 * bare blur, a 75-second mobile network blip, and a 90-second suspension. A
 * phone in a pocket with its screen off cannot answer a question any more than
 * a phone that has been killed can — so a five-second verdict declared every
 * one of those gone, and that spec caught it.
 *
 * Silence has to outlast every gap the app already tolerates before it means
 * anything. Two minutes clears the longest of them with room, and still more
 * than halves the five-minute heartbeat window this exists to beat.
 *
 * ## What would be better
 *
 * A socket closing. It is the one signal that separates "this process is gone"
 * from "this phone is briefly unreachable", and it needs no waiting at all. The
 * realtime server cannot use it yet: a `Connection` carries a user and a topic
 * set and no device identity, so it cannot tell a phone's socket from a
 * laptop's. That is the next step, not this one.
 */
const PRESENCE_TIMEOUT_MS = 120_000;

/** Re-reads the clock often enough that the verdict is never stale on screen. */
const VERDICT_TICK_MS = 1000;

/**
 * Supplies the phone-presence verdict to the connection status.
 *
 * The exchange happens inside `RealTimeProvider`, which is mounted BELOW this,
 * so the two sides talk through a store rather than through context. That is
 * the reason this component exists at all: the status provider needs an answer
 * produced by a provider it contains.
 */
export function PhonePresenceGate(t0) {
  const $ = _c(8);
  const {
    children
  } = t0;
  const probingSinceMs = useDevicePresenceStore(_temp);
  const lastPingAtMs = useDevicePresenceStore(_temp2);
  const lastPongAtMs = useDevicePresenceStore(_temp3);
  const [presence, setPresence] = useState("unknown");
  let t1;
  let t2;
  if ($[0] !== lastPingAtMs || $[1] !== lastPongAtMs || $[2] !== probingSinceMs) {
    t1 = () => {
      const evaluate = () => setPresence(phonePresenceVerdict({
        probingSinceMs,
        lastPingAtMs,
        lastPongAtMs,
        nowMs: Date.now(),
        timeoutMs: PRESENCE_TIMEOUT_MS
      }));
      const leading = setTimeout(evaluate, 0);
      const id = setInterval(evaluate, VERDICT_TICK_MS);
      return () => {
        clearTimeout(leading);
        clearInterval(id);
      };
    };
    t2 = [probingSinceMs, lastPingAtMs, lastPongAtMs];
    $[0] = lastPingAtMs;
    $[1] = lastPongAtMs;
    $[2] = probingSinceMs;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  let t3;
  if ($[5] !== children || $[6] !== presence) {
    t3 = <ConnectionStatusProvider phonePresence={presence}>{children}</ConnectionStatusProvider>;
    $[5] = children;
    $[6] = presence;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
function _temp3(state_1) {
  return state_1.lastPongAtMs;
}
function _temp2(state_0) {
  return state_0.lastPingAtMs;
}
function _temp(state) {
  return state.probingSinceMs;
}